import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    BarChart3, FileText, ClipboardCheck, Users, MessageSquare,
    Calendar, User, AlertTriangle, CreditCard,
    X,
    Edit
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { hoaProfile } from '../../lib/hoaProfile';
import { Card, SectionHeader } from '../ui';
import { motion, AnimatePresence } from 'framer-motion';
import MemberLibrary from '../member/MemberLibrary';
import MessageCenter from '../admin/MessageCenter';
import EventManagement from '../admin/EventManagement';
import { activityService, ACTION_TYPES } from '../../services/activityService';
import { supabase } from '../../lib/supabase';
import { messagingService } from '../../services/messagingService';
import { announcementService } from '../../services/announcementService';
import { Save } from 'lucide-react';
import MemberDirectory from '../common/MemberDirectory';
import ACCReview from '../common/ACCReview';
import BalanceImportPanel from '../admin/BalanceImportPanel';

const ManagementPortal = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'dashboard';
    const { tabVisibility, user, impersonating } = useAuth();
    const { hoa } = useHOA();
    const [notifications, setNotifications] = useState({});
  const notificationFetchInFlightRef = React.useRef(false);

    React.useEffect(() => {
        if (!user || !hoa?.id) return;
        const fetchNotifications = async () => {
            if (impersonating || notificationFetchInFlightRef.current) return;
            notificationFetchInFlightRef.current = true;
            try {
                const count = await messagingService.getUnreadThreadCount(user, user.portalRole || 'management', hoa.id);
                setNotifications({ messages: count });
            } catch (error) {
                console.warn('Unread message refresh failed:', error?.message || error);
            } finally {
                notificationFetchInFlightRef.current = false;
            }
        };

        fetchNotifications();
        const interval = setInterval(fetchNotifications, 5000);

        const handleRead = () => fetchNotifications();
        window.addEventListener('messages_read', handleRead);

        return () => {
            clearInterval(interval);
            window.removeEventListener('messages_read', handleRead);
        };
    }, [user, impersonating, hoa?.id]);

    const allTabs = [
        { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { id: 'documents', label: 'Documents', icon: FileText },
        { id: 'accReview', label: 'ACC Review', icon: ClipboardCheck },
        { id: 'members', label: 'Members', icon: Users },
        { id: 'messages', label: 'Inbox', icon: MessageSquare },
        { id: 'events', label: 'Events', icon: Calendar },
        { id: 'payments', label: 'Payments', icon: CreditCard },
        { id: 'profile', label: 'My Profile', icon: User },
    ];

    const tabs = allTabs.filter(tab => {
        const vis = tabVisibility?.management;
        return !vis || vis[tab.id] === undefined || vis[tab.id];
    });

    const content = {
        dashboard: <MgmtDashboard />,
        documents: <MemberLibrary onOpenAISearch={() => {}} />,
        accReview: <ACCReview />,
        members: <MemberDirectory viewerPermissionLevel={40} sectionTitle="Member Roster" />,
        messages: <MessageCenter />,
        events: <MgmtEvents />,
        payments: <BalanceImportPanel />,
        profile: <MgmtProfile />,
    };

    const handleTabChange = (tab) => {
        setSearchParams({ tab });
    };

    return (
        <PortalLayout title="Management Portal" tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} userRole="management" notifications={notifications}>
            {content[activeTab]}
        </PortalLayout>
    );
};

/* ── DASHBOARD ─────────────────────────────────────────── */
const MgmtDashboard = () => {
    const { hoa } = useHOA();
    const [announcements, setAnnouncements] = React.useState([]);
    const [newAnnouncement, setNewAnnouncement] = React.useState({ title: '', body: '', tag: 'General' });
    const [commentText, setCommentText] = React.useState({});
    const [showComments, setShowComments] = React.useState({});
    const [liveStats, setLiveStats] = React.useState({ acc: null, inquiries: null, events: null });
    const { user } = useAuth();
    const [, setSearchParams] = useSearchParams();

    const goTo = (tab) => setSearchParams({ tab });

    const fetchA = React.useCallback(async () => {
        if (!hoa?.id) return;
        const data = await announcementService.getAllAnnouncements(hoa.id);
        setAnnouncements(data);
    }, [hoa?.id]);

    React.useEffect(() => { fetchA(); }, [fetchA]);

    React.useEffect(() => {
        if (!hoa?.id) return;
        const fetchStats = async () => {
            const now = new Date().toISOString();
            const [accRes, inquiriesRes, eventsRes] = await Promise.all([
                supabase.from('message_threads').select('id', { count: 'exact', head: true })
                    .eq('hoa_id', hoa.id).eq('type', 'acc')
                    .in('status', ['submitted', 'under_review', 'additional_info_required']),
                supabase.from('message_threads').select('id', { count: 'exact', head: true })
                    .eq('hoa_id', hoa.id).eq('type', 'general').eq('status', 'open'),
                supabase.from('board_meetings').select('id', { count: 'exact', head: true })
                    .eq('hoa_id', hoa.id).gte('scheduled_at', now),
            ]);
            setLiveStats({
                acc: accRes.count ?? 0,
                inquiries: inquiriesRes.count ?? 0,
                events: eventsRes.count ?? 0,
            });
        };
        fetchStats();
    }, [hoa?.id]);

    const handlePostAnnouncement = async (e) => {
        e.preventDefault();
        if (!newAnnouncement.title.trim() || !newAnnouncement.body.trim()) return;
        await announcementService.createAnnouncement(user, newAnnouncement, hoa.id);
        setNewAnnouncement({ title: '', body: '', tag: 'General' });
        fetchA();
    };

    const handleComment = async (id) => {
        if (!commentText[id]?.trim()) return;
        await announcementService.addComment(id, user, commentText[id]);
        setCommentText({ ...commentText, [id]: '' });
        fetchA();
    };

    const toggleComments = (id) => {
        setShowComments(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div className="space-y-8">

            {/* ── HERO ── */}
            <section className="relative h-auto min-h-[260px] py-10 md:py-0 md:h-[260px] rounded-3xl overflow-hidden warm-tactile-shadow">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('/hero-bg.jpg')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-transparent" />
                <div className="relative h-full flex flex-col justify-center px-6 md:px-14 max-w-2xl">
                    <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-3">
                        {hoaProfile(hoa).shortName} · Property Management
                    </p>
                    <h2 className="text-4xl font-extrabold text-white tracking-tight mb-3">
                        Management Portal
                    </h2>
                    <p className="text-white/75 text-sm font-medium mb-8 leading-relaxed">
                        {liveStats.acc > 0
                            ? `${liveStats.acc} ACC request${liveStats.acc !== 1 ? 's' : ''} awaiting review.`
                            : 'All ACC requests up to date.'}
                    </p>
                    <div className="flex flex-wrap gap-3 md:gap-4">
                        <button
                            onClick={() => goTo('accReview')}
                            className="bg-secondary text-white px-7 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all shadow-lg"
                        >
                            ACC Review Queue
                        </button>
                        <button
                            onClick={() => goTo('members')}
                            className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-7 py-3 rounded-full font-bold text-sm hover:bg-white/20 transition-all"
                        >
                            Member Directory
                        </button>
                    </div>
                </div>
            </section>

            {/* ── STAT ROW ── */}
            <section className="bg-section-bg p-6 rounded-[2.5rem]">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-0">

                    <div className="min-h-[48px] py-4 pr-6 border-r border-white/60 flex flex-col items-center text-center">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
                            <span className="material-symbols-outlined text-[20px] text-red-400">home_work</span>
                        </div>
                        <p className="text-3xl font-extrabold text-primary-dark leading-none">
                            {liveStats.acc ?? '—'}
                        </p>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Open ACC</p>
                    </div>

                    <div className="min-h-[48px] py-4 px-6 border-r border-white/60 flex flex-col items-center text-center">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
                            <span className="material-symbols-outlined text-[20px] text-secondary">chat_bubble</span>
                        </div>
                        <p className="text-3xl font-extrabold text-primary-dark leading-none">
                            {liveStats.inquiries ?? '—'}
                        </p>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Pending Inquiries</p>
                    </div>

                    <div className="min-h-[48px] py-4 pl-6 flex flex-col items-center text-center">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
                            <span className="material-symbols-outlined text-[20px] text-secondary">calendar_today</span>
                        </div>
                        <p className="text-3xl font-extrabold text-primary-dark leading-none">
                            {liveStats.events ?? '—'}
                        </p>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Upcoming Events</p>
                    </div>

                </div>
            </section>

            {/* ── MAIN GRID ── */}
            <section className="bg-section-bg p-6 rounded-[2.5rem] space-y-6">

                {/* Row 1 — Quick Actions + Operational Snapshot */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* Quick Actions */}
                    <div className="bg-[#DFE7D8] rounded-2xl p-8 warm-tactile-shadow border border-emerald-300/20">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center border border-white shadow-sm">
                                <span className="material-symbols-outlined text-2xl text-emerald-900">bolt</span>
                            </div>
                            <h5 className="text-xl font-bold text-emerald-950">Quick Actions</h5>
                        </div>
                        <div className="space-y-3">
                            {[
                                { label: 'ACC Review Queue',  icon: 'home_work',      tab: 'accReview'  },
                                { label: 'Member Directory',  icon: 'group',          tab: 'members'    },
                                { label: 'Message Threads',   icon: 'chat_bubble',    tab: 'messages'   },
                                { label: 'Events & Meetings', icon: 'calendar_today', tab: 'events'     },
                                { label: 'Documents',         icon: 'description',    tab: 'documents'  },
                            ].map(item => (
                                <button
                                    key={item.tab}
                                    onClick={() => goTo(item.tab)}
                                    className="w-full flex items-center justify-between p-3.5 bg-white/70 hover:bg-white rounded-xl border border-white transition-all group/btn"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-xl text-emerald-800">{item.icon}</span>
                                        <span className="text-sm font-bold text-emerald-950">{item.label}</span>
                                    </div>
                                    <span className="material-symbols-outlined text-sm text-emerald-600 transition-transform group-hover/btn:translate-x-1">chevron_right</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Operational Snapshot */}
                    <div className="bg-[#1D2B20] rounded-2xl p-8 text-white warm-tactile-shadow relative overflow-hidden group">
                        <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-colors duration-500" />
                        <div className="relative z-10">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 border border-white/10">
                                <span className="material-symbols-outlined text-2xl text-secondary">insights</span>
                            </div>
                            <h5 className="text-xl font-bold mb-2">Operational Snapshot</h5>
                            <p className="text-white/60 text-xs font-medium mb-8 leading-relaxed">
                                Live community health at a glance.
                            </p>
                            <div className="space-y-5">
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-bold uppercase tracking-widest text-white/50">ACC Queue</span>
                                        <span className="text-xs font-bold text-secondary">
                                            {liveStats.acc === 0 ? 'Clear' : `${liveStats.acc ?? '…'} pending`}
                                        </span>
                                    </div>
                                    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-secondary rounded-full transition-all duration-700"
                                            style={{ width: liveStats.acc > 0 ? `${Math.min(liveStats.acc * 20, 100)}%` : '0%' }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-bold uppercase tracking-widest text-white/50">Open Inquiries</span>
                                        <span className="text-xs font-bold text-secondary">
                                            {liveStats.inquiries === 0 ? 'Clear' : `${liveStats.inquiries ?? '…'} open`}
                                        </span>
                                    </div>
                                    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-secondary rounded-full transition-all duration-700"
                                            style={{ width: liveStats.inquiries > 0 ? `${Math.min(liveStats.inquiries * 15, 100)}%` : '0%' }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-bold uppercase tracking-widest text-white/50">Upcoming Events</span>
                                        <span className="text-xs font-bold text-secondary">
                                            {liveStats.events === 0 ? 'None scheduled' : `${liveStats.events ?? '…'} scheduled`}
                                        </span>
                                    </div>
                                    <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-secondary rounded-full transition-all duration-700"
                                            style={{ width: liveStats.events > 0 ? `${Math.min(liveStats.events * 25, 100)}%` : '4%' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Row 2 — Post Announcement */}
                <div className="bg-white rounded-2xl p-8 warm-tactile-shadow border border-slate-200/50">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                            <span className="material-symbols-outlined text-2xl text-secondary">campaign</span>
                        </div>
                        <div>
                            <h5 className="text-xl font-bold text-primary-dark">Post Announcement</h5>
                            <p className="text-xs font-medium text-slate-400">Broadcast to all members</p>
                        </div>
                    </div>
                    <form onSubmit={handlePostAnnouncement} className="space-y-4">
                        <input
                            type="text"
                            placeholder="Announcement title..."
                            value={newAnnouncement.title}
                            onChange={e => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-main-bg text-sm font-medium text-primary-dark placeholder-slate-400 focus:outline-none focus:border-secondary transition-colors"
                        />
                        <textarea
                            placeholder="Write your message to the community..."
                            value={newAnnouncement.body}
                            onChange={e => setNewAnnouncement({ ...newAnnouncement, body: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-main-bg text-sm font-medium text-primary-dark placeholder-slate-400 focus:outline-none focus:border-secondary transition-colors resize-none"
                        />
                        <div className="flex items-center justify-between">
                            <select
                                value={newAnnouncement.tag}
                                onChange={e => setNewAnnouncement({ ...newAnnouncement, tag: e.target.value })}
                                className="px-4 py-2.5 rounded-full border border-slate-200 bg-white text-xs font-bold text-slate-600 focus:outline-none focus:border-secondary"
                            >
                                <option>General</option>
                                <option>Meeting</option>
                                <option>Maintenance</option>
                                <option>Infrastructure</option>
                            </select>
                            <button
                                type="submit"
                                disabled={!newAnnouncement.title || !newAnnouncement.body}
                                className="bg-secondary text-white px-7 py-2.5 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
                            >
                                Post to Community
                            </button>
                        </div>
                    </form>

                    {announcements.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Recent Posts</p>
                            {announcements.slice(0, 3).map(a => (
                                <div key={a.id} className="p-4 rounded-xl bg-main-bg border border-slate-200/60">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="text-sm font-bold text-primary-dark">{a.title}</p>
                                            <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">{a.body}</p>
                                        </div>
                                        <button
                                            onClick={() => toggleComments(a.id)}
                                            className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex-shrink-0 hover:text-secondary transition-colors"
                                        >
                                            {showComments[a.id] ? 'Hide' : 'Comments'}
                                        </button>
                                    </div>
                                    {showComments[a.id] && (
                                        <div className="mt-3 pt-3 border-t border-slate-200 flex gap-2">
                                            <input
                                                type="text"
                                                placeholder="Add a comment..."
                                                value={commentText[a.id] || ''}
                                                onChange={e => setCommentText({ ...commentText, [a.id]: e.target.value })}
                                                onKeyDown={e => e.key === 'Enter' && handleComment(a.id)}
                                                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-medium focus:outline-none focus:border-secondary"
                                            />
                                            <button
                                                onClick={() => handleComment(a.id)}
                                                disabled={!commentText[a.id]?.trim()}
                                                className="px-4 py-2 bg-secondary text-white rounded-full text-xs font-bold hover:brightness-110 transition-all disabled:opacity-50"
                                            >
                                                Post
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </section>
        </div>
    );
};

/* ACCReview moved to ../common/ACCReview (shared across portals) */

/* MgmtInquiries removed and replaced with MessageCenter */

/* ── EVENTS ────────────────────────────────────────────── */
const MgmtEvents = () => <EventManagement />;

const MgmtProfile = () => {
    const { user } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [showApprovalMock, setShowApprovalMock] = useState(false);
    const [formData, setFormData] = useState({
        name: user?.name || '',
        title: user?.title || '',
        company: user?.company || '',
        email: user?.email || '',
        phone: user?.phone || ''
    });

    const handleSave = () => {
        setIsEditing(false);
        setShowApprovalMock(true);
        setTimeout(() => setShowApprovalMock(false), 4000);

        activityService.log(ACTION_TYPES.PROFILE_EDIT_REQUESTED, user, {
            details: 'Management portal profile update request'
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <SectionHeader icon={User} title="My Profile" color="text-amber-700" accent="bg-amber-100" />
                {!isEditing && (
                    <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-amber-300 text-slate-700 rounded-xl transition-all">
                        <Edit className="w-4 h-4" /> Edit Profile
                    </button>
                )}
            </div>

            <AnimatePresence>
                {showApprovalMock && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                            <h4 className="font-semibold text-sm">Profile Update Received</h4>
                            <p className="text-xs mt-1">Your profile changes have been submitted to the HOA Board for approval. They will be reflected here once approved.</p>
                        </div>
                        <button onClick={() => setShowApprovalMock(false)} className="ml-auto text-amber-500 hover:text-amber-700"><X className="w-4 h-4" /></button>
                    </motion.div>
                )}
            </AnimatePresence>

            <Card>
                {isEditing ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Name</label>
                                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-lg text-base md:text-sm outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Title</label>
                                <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-lg text-base md:text-sm outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Company</label>
                                <input type="text" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-lg text-base md:text-sm outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Email</label>
                                <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-lg text-base md:text-sm outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Phone</label>
                                <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-lg text-base md:text-sm outline-none" />
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                            <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors">Cancel</button>
                            <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium transition-colors"><Save className="w-4 h-4" /> Save Request</button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-5">
                        <div><span className="block text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Name</span><span className="text-slate-800 font-medium">{user?.name || '—'}</span></div>
                        <div className="border-t border-slate-100 pt-4"><span className="block text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Title</span><span className="text-slate-800 font-medium">{user?.title || '—'}</span></div>
                        <div className="border-t border-slate-100 pt-4"><span className="block text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Company</span><span className="text-slate-800 font-medium">{user?.company || '—'}</span></div>
                        <div className="border-t border-slate-100 pt-4"><span className="block text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Email</span><span className="text-slate-800 font-medium">{user?.email || '—'}</span></div>
                        <div className="border-t border-slate-100 pt-4"><span className="block text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Phone</span><span className="text-slate-800 font-medium">{user?.phone || '—'}</span></div>
                        <div className="border-t border-slate-100 pt-4">
                            <span className="block text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Role</span>
                            <span className="px-2.5 py-0.5 text-xs rounded-full border font-medium bg-amber-50 text-amber-800 border-amber-200">Management</span>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default ManagementPortal;
